import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Layout } from '../../components/Layout';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Badge } from '../../components/ui/badge';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from '../../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Switch } from '../../components/ui/switch';
import { Separator } from '../../components/ui/separator';
import { toast } from 'sonner';
import { Shield, ArrowLeft, Plus, Edit, Trash2, Settings2, Eye, Lock } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { DATA_SCOPE, MODULES, getScopeName, getDefaultPermissions } from '../../utils/permissions';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export const RolesSettings = () => {
  const navigate = useNavigate();
  const [roles, setRoles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [permissionsDialogOpen, setPermissionsDialogOpen] = useState(false);
  const [selectedRole, setSelectedRole] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    display_name: '',
    description: ''
  });
  const [permissions, setPermissions] = useState(getDefaultPermissions());

  useEffect(() => {
    fetchRoles();
  }, []);

  const fetchRoles = async () => {
    try {
      const response = await axios.get(`${API}/roles`);
      setRoles(response.data);
    } catch (error) {
      console.error('Failed to fetch roles:', error);
      toast.error('Failed to load roles');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateRole = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API}/roles`, formData);
      toast.success('Role created successfully');
      setCreateDialogOpen(false);
      setFormData({ name: '', display_name: '', description: '' });
      fetchRoles();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create role');
    }
  };

  const handleUpdateRole = async (e) => {
    e.preventDefault();
    try {
      await axios.patch(`${API}/roles/${selectedRole.id}`, formData);
      toast.success('Role updated successfully');
      setEditDialogOpen(false);
      setSelectedRole(null);
      fetchRoles();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update role');
    }
  };

  const handleDeleteRole = async (roleId, roleName) => {
    if (!window.confirm(`Are you sure you want to delete the role "${roleName}"? This action cannot be undone.`)) return;
    
    try {
      await axios.delete(`${API}/roles/${roleId}`);
      toast.success('Role deleted successfully');
      fetchRoles();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete role');
    }
  };

  const openEditDialog = (role) => {
    setSelectedRole(role);
    setFormData({
      name: role.name,
      display_name: role.display_name,
      description: role.description || ''
    });
    setEditDialogOpen(true);
  };

  const openPermissionsDialog = (role) => {
    setSelectedRole(role);
    setPermissions(role.permissions || getDefaultPermissions());
    setPermissionsDialogOpen(true);
  };

  const handleUpdatePermissions = async () => {
    try {
      await axios.patch(`${API}/roles/${selectedRole.id}`, { permissions });
      toast.success('Permissions updated successfully');
      setPermissionsDialogOpen(false);
      fetchRoles();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update permissions');
    }
  };

  const updateDataScope = (scope) => {
    setPermissions({
      ...permissions,
      data_access: { scope }
    });
  };

  const updateModulePermission = (module, action, value) => {
    setPermissions({
      ...permissions,
      [module]: {
        ...permissions[module],
        [action]: value
      }
    });
  };

  if (loading) {
    return (
      <Layout>
        <div className="text-center py-12">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto"></div>
          <p className="mt-4 text-muted-foreground">Loading roles...</p>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-4">
            <Button variant="ghost" onClick={() => navigate('/settings')}>
              <ArrowLeft className="h-4 w-4 mr-2" />
              Back
            </Button>
            <div>
              <h1 className="text-3xl font-bold">Roles Management</h1>
              <p className="text-muted-foreground">Create and manage custom user roles</p>
            </div>
          </div>
        </div>

        <div className="flex justify-end">
          <Dialog open={createDialogOpen} onOpenChange={setCreateDialogOpen}>
            <DialogTrigger asChild>
              <Button>
                <Plus className="h-4 w-4 mr-2" />
                Create Role
              </Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>Create New Role</DialogTitle>
                <DialogDescription>Add a custom role to your organization</DialogDescription>
              </DialogHeader>
              <form onSubmit={handleCreateRole} className="space-y-4">
                <div>
                  <Label>Role Name (System ID) *</Label>
                  <Input
                    value={formData.name}
                    onChange={(e) => setFormData({...formData, name: e.target.value.toLowerCase().replace(/\s+/g, '_')})}
                    placeholder="e.g., field_technician"
                    required
                  />
                  <p className="text-xs text-muted-foreground mt-1">
                    Use lowercase letters and underscores only (e.g., field_technician)
                  </p>
                </div>
                <div>
                  <Label>Display Name *</Label>
                  <Input
                    value={formData.display_name}
                    onChange={(e) => setFormData({...formData, display_name: e.target.value})}
                    placeholder="e.g., Field Technician"
                    required
                  />
                  <p className="text-xs text-muted-foreground mt-1">
                    This is what users will see in the interface
                  </p>
                </div>
                <div>
                  <Label>Description</Label>
                  <Textarea
                    value={formData.description}
                    onChange={(e) => setFormData({...formData, description: e.target.value})}
                    placeholder="Brief description of this role's responsibilities"
                    rows={3}
                  />
                </div>
                <DialogFooter>
                  <Button type="button" variant="outline" onClick={() => setCreateDialogOpen(false)}>Cancel</Button>
                  <Button type="submit">Create Role</Button>
                </DialogFooter>
              </form>
            </DialogContent>
          </Dialog>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {roles.map(role => (
            <Card key={role.id} className="hover:shadow-lg transition-shadow">
              <CardHeader>
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-center space-x-2 mb-2">
                      <Shield className="h-5 w-5 text-primary" />
                      <CardTitle className="text-lg">{role.display_name}</CardTitle>
                    </div>
                    <CardDescription>
                      {role.description || 'No description provided'}
                    </CardDescription>
                  </div>
                  {role.is_system && (
                    <Badge variant="secondary" className="text-xs">System</Badge>
                  )}
                </div>
              </CardHeader>
              <CardContent>
                <div className="space-y-2 mb-4">
                  <div className="text-sm">
                    <span className="text-muted-foreground">Role ID:</span>
                    <span className="ml-2 font-mono text-xs">{role.name}</span>
                  </div>
                  <div className="text-sm">
                    <span className="text-muted-foreground">Status:</span>
                    {role.is_active ? (
                      <Badge className="ml-2 bg-green-600">Active</Badge>
                    ) : (
                      <Badge className="ml-2" variant="secondary">Inactive</Badge>
                    )}
                  </div>
                </div>
                <div className="space-y-2">
                  <Button
                    variant="default"
                    size="sm"
                    className="w-full"
                    onClick={() => openPermissionsDialog(role)}
                  >
                    <Settings2 className="h-4 w-4 mr-2" />
                    Configure Permissions
                  </Button>
                  {!role.is_system && (
                    <div className="flex space-x-2">
                      <Button
                        variant="outline"
                        size="sm"
                        className="flex-1"
                        onClick={() => openEditDialog(role)}
                      >
                        <Edit className="h-4 w-4 mr-2" />
                        Edit
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handleDeleteRole(role.id, role.display_name)}
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  )}
                  {role.is_system && (
                    <p className="text-xs text-muted-foreground text-center py-1">
                      Basic info cannot be edited
                    </p>
                  )}
                </div>
              </CardContent>
            </Card>
          ))}
        </div>

        {/* Edit Dialog */}
        <Dialog open={editDialogOpen} onOpenChange={setEditDialogOpen}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Edit Role</DialogTitle>
              <DialogDescription>Update role information</DialogDescription>
            </DialogHeader>
            <form onSubmit={handleUpdateRole} className="space-y-4">
              <div>
                <Label>Role Name (System ID) *</Label>
                <Input
                  value={formData.name}
                  onChange={(e) => setFormData({...formData, name: e.target.value.toLowerCase().replace(/\s+/g, '_')})}
                  required
                />
                <p className="text-xs text-muted-foreground mt-1">
                  Use lowercase letters and underscores only
                </p>
              </div>
              <div>
                <Label>Display Name *</Label>
                <Input
                  value={formData.display_name}
                  onChange={(e) => setFormData({...formData, display_name: e.target.value})}
                  required
                />
              </div>
              <div>
                <Label>Description</Label>
                <Textarea
                  value={formData.description}
                  onChange={(e) => setFormData({...formData, description: e.target.value})}
                  rows={3}
                />
              </div>
              <DialogFooter>
                <Button type="button" variant="outline" onClick={() => setEditDialogOpen(false)}>Cancel</Button>
                <Button type="submit">Update Role</Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>

        {/* Permissions Configuration Dialog */}
        <Dialog open={permissionsDialogOpen} onOpenChange={setPermissionsDialogOpen}>
          <DialogContent className="max-w-3xl max-h-[80vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Shield className="h-5 w-5" />
                Configure Permissions: {selectedRole?.display_name}
              </DialogTitle>
              <DialogDescription>
                Define data access scope and module-level permissions for this role
              </DialogDescription>
            </DialogHeader>
            
            <div className="space-y-6">
              {/* Data Access Scope */}
              <div className="space-y-3">
                <Label className="text-base font-semibold flex items-center gap-2">
                  <Eye className="h-4 w-4" />
                  Data Access Scope
                </Label>
                <p className="text-sm text-muted-foreground">
                  Controls what data users with this role can view
                </p>
                <Select 
                  value={permissions?.data_access?.scope || DATA_SCOPE.OWN}
                  onValueChange={updateDataScope}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value={DATA_SCOPE.ALL}>
                      <div className="flex flex-col items-start">
                        <span className="font-medium">{getScopeName(DATA_SCOPE.ALL)}</span>
                        <span className="text-xs text-muted-foreground">See all records in the system</span>
                      </div>
                    </SelectItem>
                    <SelectItem value={DATA_SCOPE.OWN}>
                      <div className="flex flex-col items-start">
                        <span className="font-medium">{getScopeName(DATA_SCOPE.OWN)}</span>
                        <span className="text-xs text-muted-foreground">See only records assigned to them</span>
                      </div>
                    </SelectItem>
                    <SelectItem value={DATA_SCOPE.TEAM}>
                      <div className="flex flex-col items-start">
                        <span className="font-medium">{getScopeName(DATA_SCOPE.TEAM)}</span>
                        <span className="text-xs text-muted-foreground">See records from their team members</span>
                      </div>
                    </SelectItem>
                    <SelectItem value={DATA_SCOPE.MARKET}>
                      <div className="flex flex-col items-start">
                        <span className="font-medium">{getScopeName(DATA_SCOPE.MARKET)}</span>
                        <span className="text-xs text-muted-foreground">See records in their markets</span>
                      </div>
                    </SelectItem>
                    <SelectItem value={DATA_SCOPE.TEAM_AND_MARKET}>
                      <div className="flex flex-col items-start">
                        <span className="font-medium">{getScopeName(DATA_SCOPE.TEAM_AND_MARKET)}</span>
                        <span className="text-xs text-muted-foreground">See team & market records</span>
                      </div>
                    </SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <Separator />

              {/* Module Permissions */}
              <div className="space-y-3">
                <Label className="text-base font-semibold flex items-center gap-2">
                  <Lock className="h-4 w-4" />
                  Module Permissions
                </Label>
                <p className="text-sm text-muted-foreground">
                  Control what actions users can perform in each module
                </p>
                
                <div className="space-y-4">
                  {Object.entries(MODULES).map(([key, module]) => (
                    <Card key={module} className="p-4">
                      <div className="space-y-3">
                        <h4 className="font-medium capitalize">{module.replace('_', ' ')}</h4>
                        <div className="grid grid-cols-3 gap-4">
                          <div className="flex items-center justify-between">
                            <Label className="text-sm">Read</Label>
                            <Switch
                              checked={permissions?.[module]?.read || false}
                              onCheckedChange={(checked) => updateModulePermission(module, 'read', checked)}
                            />
                          </div>
                          <div className="flex items-center justify-between">
                            <Label className="text-sm">Write</Label>
                            <Switch
                              checked={permissions?.[module]?.write || false}
                              onCheckedChange={(checked) => updateModulePermission(module, 'write', checked)}
                            />
                          </div>
                          <div className="flex items-center justify-between">
                            <Label className="text-sm">Delete</Label>
                            <Switch
                              checked={permissions?.[module]?.delete || false}
                              onCheckedChange={(checked) => updateModulePermission(module, 'delete', checked)}
                            />
                          </div>
                        </div>
                      </div>
                    </Card>
                  ))}
                </div>
              </div>
            </div>

            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setPermissionsDialogOpen(false)}>
                Cancel
              </Button>
              <Button onClick={handleUpdatePermissions}>
                Save Permissions
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Info Card */}
        <Card className="bg-blue-50 dark:bg-blue-950 border-blue-200 dark:border-blue-800">
          <CardContent className="pt-6">
            <div className="flex items-start space-x-3">
              <Shield className="h-5 w-5 text-blue-600 mt-0.5" />
              <div>
                <h4 className="font-semibold text-blue-900 dark:text-blue-100">About Roles</h4>
                <p className="text-sm text-blue-700 dark:text-blue-300 mt-1">
                  Roles define user access levels in your organization. System roles (marked with "System" badge) cannot be modified or deleted. 
                  Custom roles can be created to match your specific organizational needs.
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    </Layout>
  );
};